import { useLang } from "@/i18n/LanguageContext";
import { heroBg } from "@/data/media";
import { ChevronRight, MapPin, Star, Flame, Phone } from "lucide-react";

const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-24 md:pt-32 pb-16 overflow-hidden bg-[#0a0a0a]"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBg}
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/85 to-[#0a0a0a]" />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Vertical tag left */}
      <div className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <p className="vertical-rl font-mono text-[10px] tracking-[0.4em] text-[#71717a] uppercase">
          PL · Nowy Dwór Mazowiecki · est. 2018
        </p>
      </div>

      {/* Vertical phone right */}
      <a
        href="tel:+48513066040"
        data-testid="hero-vertical-phone"
        className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 items-center gap-2 hover:text-[#dc2626] transition-colors"
      >
        <p className="vertical-rl font-mono text-[11px] tracking-[0.3em] text-[#a1a1aa] uppercase">
          +48 513 066 040 · zadzwoń
        </p>
      </a>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Tag */}
        <div className="flex items-center gap-3 mb-8 fade-up">
          <span className="w-10 h-px bg-[#dc2626]" />
          <p
            data-testid="hero-tag"
            className="font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#dc2626]"
          >
            {t("hero.tag")}
          </p>
        </div>

        {/* Title */}
        <h1
          data-testid="hero-title"
          className="font-display font-black uppercase leading-[0.92] tracking-tighter text-white max-w-5xl fade-up"
          style={{ animationDelay: "0.1s" }}
        >
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            {t("hero.title1")}
          </span>
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#71717a]">
            {t("hero.title2")}
          </span>
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            <span className="text-[#dc2626]">{t("hero.title3")}</span>
          </span>
        </h1>

        <div
          className="grid lg:grid-cols-12 gap-10 mt-12 fade-up"
          style={{ animationDelay: "0.25s" }}
        >
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed max-w-2xl">
              {t("hero.lead")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#dc2626] text-white font-bold uppercase tracking-wider text-xs px-7 py-4 hover:bg-[#b91c1c] transition-colors"
              >
                {t("hero.ctaPrimary")}
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href="#gallery"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-[#27272a] text-white font-bold uppercase tracking-wider text-xs px-7 py-4 hover:border-[#dc2626] hover:text-[#dc2626] transition-colors"
              >
                {t("hero.ctaSecondary")}
              </a>
              <a
                href="tel:+48513066040"
                data-testid="hero-cta-call"
                className="sm:hidden inline-flex items-center justify-center gap-2 border border-[#27272a] text-white font-bold uppercase tracking-wider text-xs px-7 py-4"
              >
                <Phone className="w-4 h-4" /> 513 066 040
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 lg:border-l lg:border-[#1f1f1f] lg:pl-10 flex flex-col justify-end gap-5">
            <MetaRow
              icon={<MapPin className="w-4 h-4" />}
              label="01"
              text={t("hero.meta.loc")}
            />
            <MetaRow
              icon={<Star className="w-4 h-4 fill-[#dc2626] text-[#dc2626]" />}
              label="02"
              text={t("hero.meta.rating")}
            />
            <MetaRow
              icon={<Flame className="w-4 h-4" />}
              label="03"
              text={t("hero.meta.furnace")}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const MetaRow = ({ icon, label, text }) => (
  <div className="flex items-center gap-4 border-b border-[#1f1f1f] pb-4">
    <span className="font-mono text-[10px] text-[#52525b] tracking-[0.2em]">
      {label}
    </span>
    <span className="text-[#dc2626]">{icon}</span>
    <span className="text-sm text-white">{text}</span>
  </div>
);

export default Hero;
